/**
 * Hook pour rechercher les prospects par code BPI ou nom d'équipage
 * Retourne les contacts associés pour l'association automatique à un équipage
 */
import { useCallback, useState } from 'react';
import type { CrewMemberRole } from '../types/crew';
import { useSupabase } from './useSupabase';

/**
 * Contact trouvé via le code BPI
 */
export interface BpiContact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  /** Rôle déterminé depuis form_fields.target_role */
  role?: CrewMemberRole;
}

/**
 * Résultat de la recherche BPI
 */
export interface BpiLookupResult {
  found: boolean;
  contacts: BpiContact[];
  prospectCount: number;
  /** Nom de l'équipage renseigné dans le formulaire BPI */
  crewName?: string;
  /** ID du produit associé au formulaire */
  productId?: string;
  /** Nom du produit associé au formulaire */
  productName?: string;
  /** Code BPI (utile pour la recherche par nom) */
  bpiCode?: string;
}

/**
 * Résultats multiples pour la recherche par nom d'équipage
 */
export interface BpiLookupMultipleResults {
  found: boolean;
  results: BpiLookupResult[];
}

/**
 * Hook pour rechercher les contacts associés à un code BPI ou nom d'équipage
 * Limite les résultats à 2 contacts maximum (format duo pilote/copilote)
 */
export function useBpiLookup() {
  const supabase = useSupabase();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BpiLookupResult | null>(null);
  const [multipleResults, setMultipleResults] = useState<BpiLookupMultipleResults | null>(null);

  /**
   * Extrait le nom d'équipage depuis les données de soumission
   */
  const extractCrewName = (
    data: Record<string, unknown>,
    fieldLabels: Record<string, string>
  ): string | undefined => {
    for (const [fieldId, value] of Object.entries(data)) {
      if (value && typeof value === 'string' && value.trim()) {
        const label = fieldLabels[fieldId]?.toLowerCase() || '';
        if (label.includes('nom de l') && label.includes('quipage')) {
          return value.trim();
        }
      }
    }
    return undefined;
  };

  /**
   * Rechercher les contacts par code BPI
   * @param bpiCode - Code BPI au format BPI-XXXXXX
   */
  const lookup = useCallback(
    async (bpiCode: string): Promise<BpiLookupResult> => {
      // Nettoyer le code BPI (trim et uppercase)
      const cleanCode = bpiCode.trim().toUpperCase();

      if (!cleanCode) {
        const emptyResult: BpiLookupResult = { found: false, contacts: [], prospectCount: 0 };
        setResult(emptyResult);
        return emptyResult;
      }

      setLoading(true);
      setError(null);
      setMultipleResults(null);

      try {
        // Rechercher les form_submissions avec ce bpi_code
        const { data: submissions, error: fetchError } = await supabase
          .from('form_submissions')
          .select(
            `
            id,
            data,
            bpi_code,
            contact_id,
            form:forms(
              id,
              product_id,
              product:products(id, name),
              fields:form_fields(id, label, field_template:field_templates(id, label, name, target_role))
            )
          `
          )
          .eq('bpi_code', cleanCode);

        if (fetchError) {
          throw fetchError;
        }

        if (!submissions || submissions.length === 0) {
          const emptyResult: BpiLookupResult = {
            found: false,
            contacts: [],
            prospectCount: 0,
            bpiCode: cleanCode,
          };
          setResult(emptyResult);
          return emptyResult;
        }

        // Récupérer tous les contact_id uniques
        const contactIds = Array.from(
          new Set(submissions.map((s: any) => s.contact_id).filter((id) => id))
        );

        // Récupérer les infos des contacts
        const { data: contacts, error: contactsError } = await supabase
          .from('contacts')
          .select('id, first_name, last_name, email')
          .in('id', contactIds);

        if (contactsError) {
          throw contactsError;
        }

        // Pour les formulaires DUO, déterminer le rôle de chaque contact
        // en regardant quels champs ont été remplis dans submission.data
        const contactsWithRole: BpiContact[] = (contacts || []).map((contact: any) => {
          // Trouver la soumission correspondante
          const submission: any = submissions.find((s: any) => s.contact_id === contact.id);
          let role: CrewMemberRole | undefined;

          // Extraire form (peut être un tableau ou un objet unique)
          const form = submission?.form
            ? Array.isArray(submission.form)
              ? submission.form[0]
              : submission.form
            : null;
          const fields = form?.fields
            ? Array.isArray(form.fields)
              ? form.fields
              : [form.fields]
            : [];

          if (submission?.data && fields.length > 0) {
            // Chercher si des champs avec target_role='pilot' ou 'copilot' ont des valeurs
            const pilotFields = fields.filter((f: any) => {
              const template = Array.isArray(f.field_template)
                ? f.field_template[0]
                : f.field_template;
              return template?.target_role === 'pilot';
            });
            const copilotFields = fields.filter((f: any) => {
              const template = Array.isArray(f.field_template)
                ? f.field_template[0]
                : f.field_template;
              return template?.target_role === 'copilot';
            });

            // Vérifier quel set de champs a été rempli
            const hasPilotData = pilotFields.some((f: any) =>
              Object.prototype.hasOwnProperty.call(submission.data, f.id)
            );
            const hasCopilotData = copilotFields.some((f: any) =>
              Object.prototype.hasOwnProperty.call(submission.data, f.id)
            );

            if (hasPilotData && !hasCopilotData) {
              role = 'pilot';
            } else if (hasCopilotData && !hasPilotData) {
              role = 'copilot';
            } else if (hasPilotData && hasCopilotData) {
              // Les deux sont remplis, prendre pilot par défaut pour le 1er contact
              role = contactIds.indexOf(contact.id) === 0 ? 'pilot' : 'copilot';
            }
          }

          return {
            id: contact.id,
            first_name: contact.first_name || '',
            last_name: contact.last_name || '',
            email: contact.email || '',
            role,
          };
        });

        // Extraire les infos du form (première soumission)
        const firstSubmission: any = submissions[0];
        let crewName: string | undefined;
        let productId: string | undefined;
        let productName: string | undefined;

        if (firstSubmission) {
          const firstForm = firstSubmission.form
            ? Array.isArray(firstSubmission.form)
              ? firstSubmission.form[0]
              : firstSubmission.form
            : null;
          const firstProduct = firstForm?.product
            ? Array.isArray(firstForm.product)
              ? firstForm.product[0]
              : firstForm.product
            : null;
          const firstFields = firstForm?.fields
            ? Array.isArray(firstForm.fields)
              ? firstForm.fields
              : [firstForm.fields]
            : [];

          if (firstProduct) {
            productId = firstProduct.id;
            productName = firstProduct.name;
          }

          if (firstSubmission.data && firstFields.length > 0) {
            const fieldLabels: Record<string, string> = {};
            firstFields.forEach((f: any) => {
              const template = Array.isArray(f.field_template)
                ? f.field_template[0]
                : f.field_template;
              const label = f.label || template?.label || template?.name || '';
              fieldLabels[f.id] = label;
              if (template?.id) fieldLabels[template.id] = label;
            });
            crewName = extractCrewName(
              firstSubmission.data as Record<string, unknown>,
              fieldLabels
            );
          }
        }

        const lookupResult: BpiLookupResult = {
          found: contactsWithRole.length > 0,
          contacts: contactsWithRole.slice(0, 2),
          prospectCount: submissions.length,
          crewName,
          productId,
          productName,
          bpiCode: cleanCode,
        };

        setResult(lookupResult);
        return lookupResult;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la recherche BPI';
        setError(errorMessage);
        console.error('[useBpiLookup] Erreur:', err);

        const errorResult: BpiLookupResult = { found: false, contacts: [], prospectCount: 0 };
        setResult(errorResult);
        return errorResult;
      } finally {
        setLoading(false);
      }
    },
    [supabase]
  );

  /**
   * Rechercher par nom d'équipage (peut retourner plusieurs BPI)
   * @param crewName - Nom de l'équipage à rechercher (recherche partielle)
   */
  const lookupByCrewName = useCallback(
    async (searchName: string): Promise<BpiLookupMultipleResults> => {
      const cleanName = searchName.trim().toLowerCase();

      if (!cleanName || cleanName.length < 2) {
        const emptyResult: BpiLookupMultipleResults = { found: false, results: [] };
        setMultipleResults(emptyResult);
        return emptyResult;
      }

      setLoading(true);
      setError(null);
      setResult(null);

      try {
        // Rechercher toutes les form_submissions qui ont un bpi_code
        const { data: submissions, error: fetchError } = await supabase
          .from('form_submissions')
          .select(
            `
            id,
            data,
            bpi_code,
            contact_id,
            form:forms(
              id,
              product_id,
              product:products(id, name),
              fields:form_fields(id, label, field_template:field_templates(id, label, name, target_role))
            )
          `
          )
          .not('bpi_code', 'is', null);

        if (fetchError) throw fetchError;

        // Grouper les résultats par bpi_code
        const bpiGroups = new Map<string, any[]>();

        (submissions || []).forEach((submission: any) => {
          if (!submission.bpi_code || !submission.form) return;

          const form = Array.isArray(submission.form) ? submission.form[0] : submission.form;
          const fields = form?.fields
            ? Array.isArray(form.fields)
              ? form.fields
              : [form.fields]
            : [];

          if (fields.length === 0) return;

          // Créer le mapping des labels
          const fieldLabels: Record<string, string> = {};
          fields.forEach((f: any) => {
            const template = Array.isArray(f.field_template)
              ? f.field_template[0]
              : f.field_template;
            const label = f.label || template?.label || template?.name || '';
            fieldLabels[f.id] = label;
            if (template?.id) fieldLabels[template.id] = label;
          });

          // Extraire le nom d'équipage
          const foundCrewName = extractCrewName(
            (submission.data || {}) as Record<string, unknown>,
            fieldLabels
          );

          // Vérifier si le nom correspond (recherche partielle insensible à la casse)
          if (foundCrewName && foundCrewName.toLowerCase().includes(cleanName)) {
            const bpiCode = submission.bpi_code;
            if (!bpiGroups.has(bpiCode)) {
              bpiGroups.set(bpiCode, []);
            }
            bpiGroups.get(bpiCode)!.push({
              ...submission,
              _crewName: foundCrewName,
            });
          }
        });

        // Récupérer tous les contacts en une seule requête
        const allContactIds = Array.from(
          new Set(
            Array.from(bpiGroups.values())
              .flat()
              .map((s: any) => s.contact_id)
              .filter((id) => id)
          )
        );

        const { data: allContacts, error: contactsError } = await supabase
          .from('contacts')
          .select('id, first_name, last_name, email')
          .in('id', allContactIds);

        if (contactsError) throw contactsError;

        const contactsById = new Map((allContacts || []).map((c: any) => [c.id, c]));

        // Convertir les groupes en résultats
        const results: BpiLookupResult[] = [];

        bpiGroups.forEach((groupSubmissions, bpiCode) => {
          const contactIds = Array.from(
            new Set(groupSubmissions.map((s: any) => s.contact_id).filter((id) => id))
          );

          const contactsWithRole: BpiContact[] = contactIds
            .map((contactId) => {
              const contact = contactsById.get(contactId);
              if (!contact) return null;

              const submission: any = groupSubmissions.find((s: any) => s.contact_id === contactId);
              let role: CrewMemberRole | undefined;

              // Extraire form (peut être un tableau ou un objet unique)
              const form = submission?.form
                ? Array.isArray(submission.form)
                  ? submission.form[0]
                  : submission.form
                : null;
              const fields = form?.fields
                ? Array.isArray(form.fields)
                  ? form.fields
                  : [form.fields]
                : [];

              if (submission?.data && fields.length > 0) {
                const pilotFields = fields.filter((f: any) => {
                  const template = Array.isArray(f.field_template)
                    ? f.field_template[0]
                    : f.field_template;
                  return template?.target_role === 'pilot';
                });
                const copilotFields = fields.filter((f: any) => {
                  const template = Array.isArray(f.field_template)
                    ? f.field_template[0]
                    : f.field_template;
                  return template?.target_role === 'copilot';
                });

                const hasPilotData = pilotFields.some((f: any) =>
                  Object.prototype.hasOwnProperty.call(submission.data, f.id)
                );
                const hasCopilotData = copilotFields.some((f: any) =>
                  Object.prototype.hasOwnProperty.call(submission.data, f.id)
                );

                if (hasPilotData && !hasCopilotData) {
                  role = 'pilot';
                } else if (hasCopilotData && !hasPilotData) {
                  role = 'copilot';
                } else if (hasPilotData && hasCopilotData) {
                  role = contactIds.indexOf(contactId) === 0 ? 'pilot' : 'copilot';
                }
              }

              return {
                id: contact.id,
                first_name: contact.first_name || '',
                last_name: contact.last_name || '',
                email: contact.email || '',
                role,
              } as BpiContact;
            })
            .filter((c) => c !== null) as BpiContact[];

          const firstSubmission: any = groupSubmissions[0];
          const firstForm = firstSubmission?.form
            ? Array.isArray(firstSubmission.form)
              ? firstSubmission.form[0]
              : firstSubmission.form
            : null;
          const firstProduct = firstForm?.product
            ? Array.isArray(firstForm.product)
              ? firstForm.product[0]
              : firstForm.product
            : null;

          results.push({
            found: true,
            contacts: contactsWithRole.slice(0, 2),
            prospectCount: groupSubmissions.length,
            crewName: firstSubmission._crewName,
            productId: firstProduct?.id,
            productName: firstProduct?.name,
            bpiCode,
          });
        });

        const lookupResults: BpiLookupMultipleResults = {
          found: results.length > 0,
          results,
        };

        setMultipleResults(lookupResults);
        return lookupResults;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Erreur lors de la recherche par nom';
        setError(errorMessage);
        console.error('[useBpiLookup] Erreur lookupByCrewName:', err);

        const errorResult: BpiLookupMultipleResults = { found: false, results: [] };
        setMultipleResults(errorResult);
        return errorResult;
      } finally {
        setLoading(false);
      }
    },
    [supabase]
  );

  /**
   * Réinitialiser l'état du hook
   */
  const reset = useCallback(() => {
    setResult(null);
    setMultipleResults(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    lookup,
    lookupByCrewName,
    reset,
    loading,
    error,
    result,
    multipleResults,
  };
}
